import {
  patientMergeClient,
  type PatientMergePlan,
} from "@admin/patientMergeClient";
import { entitiesKeys } from "@entities/entitiesQueries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tasksKeys } from "@tasks/tasksQueries";
import { visitsKeys } from "@visits/visitsQueries";

const patientMergeKeys = {
  all: ["patient-merge"] as const,
  plan: (keep: string, absorb: string) =>
    ["patient-merge", keep, absorb] as const,
};

/** No retry: a bad pair (same uuid, or a 403 for non-leadership) won't resolve on retry. */
export const usePatientMergePlan = (
  keepUuid: string | null,
  absorbUuid: string | null,
) =>
  useQuery({
    queryKey: patientMergeKeys.plan(keepUuid ?? "", absorbUuid ?? ""),
    queryFn: (): Promise<PatientMergePlan> =>
      patientMergeClient.getPlan(keepUuid!, absorbUuid!),
    enabled: !!keepUuid && !!absorbUuid && keepUuid !== absorbUuid,
    retry: false,
  });

/** A merge repoints or deletes nearly everything the absorbed patient held, so invalidation fans out wide. */
export const useMergePatients = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      keepUuid,
      absorbUuid,
    }: {
      keepUuid: string;
      absorbUuid: string;
    }) => patientMergeClient.apply(keepUuid, absorbUuid),
    meta: { action: "merge patients", handled: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entitiesKeys.all });
      queryClient.invalidateQueries({ queryKey: visitsKeys.all });
      queryClient.invalidateQueries({ queryKey: tasksKeys.all });
      queryClient.invalidateQueries({ queryKey: ["view"] });
      queryClient.invalidateQueries({ queryKey: ["views"] });
      queryClient.invalidateQueries({ queryKey: patientMergeKeys.all });
    },
  });
};
